import React, { useState, useContext } from 'react';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { X, Eye, EyeOff, Mail, Lock, User, HelpCircle } from 'lucide-react';
import ForgotPassword from './ForgotPassword';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { AuthContext } from '../App';
import { isMobileApp } from '../utils/platform';
import { openExternalBrowser } from '../utils/platform';

const API = process.env.REACT_APP_BACKEND_URL;

const PREDEFINED_QUESTIONS = [
  "What is your favorite book?",
  "What was your first pet's name?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was your childhood nickname?",
  "Write your own question"
];

const AuthModal = ({ mode, onClose, onAuth, onSwitchMode }) => {
  const { handleLogin } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    security_question: '',
    security_answer: '',
    website: ''  // Honeypot field
  });
  const [customQuestion, setCustomQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const isRegister = mode === 'register';

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (isRegister && !formData.full_name) {
      newErrors.full_name = 'Full name is required';
    }
    
    // Security question is optional - only needed for password recovery
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Get device fingerprint for single-device login
      const deviceFingerprint = await getDeviceFingerprint();
      
      // Prepare data with proper security question and device fingerprint
      const submitData = {
        ...formData,
        security_question: formData.security_question === 'Write your own question' 
          ? customQuestion 
          : formData.security_question,
        device_fingerprint: deviceFingerprint
      };
      
      await onAuth(submitData, isRegister);
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <>
    <Dialog open={!showForgotPassword} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-3xl sm:rounded-3xl p-0 overflow-hidden border-0 shadow-2xl">
        {/* Gradient header — compact */}
        <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-6 py-4 text-center">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
          <div className="relative">
            <h2 className="text-lg font-bold text-white tracking-tight leading-tight">
              {isRegister ? 'Join Nerd Nation' : 'Welcome back'}
            </h2>
            <p className="text-xs text-white/85 mt-0.5">
              {isRegister
                ? 'Get 30 free AI credits when you sign up'
                : 'Sign in to continue your journey'}
            </p>
          </div>
        </div>

        <DialogHeader className="sr-only">
          <DialogTitle>{isRegister ? 'Create Account' : 'Sign In'}</DialogTitle>
        </DialogHeader>

        <div className="px-5 pt-4 pb-5 space-y-4">
          {/* ── Google Sign-In (direct popup, no Emergent intermediate page) ── */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          {isMobileApp() ? (
            /* Inside the Android WebView Google blocks its own OAuth button.
               Route Google login through the real Chrome browser via CCT, then
               deep-link the JWT back into the app. */
            <button
              type="button"
              data-testid="mobile-google-login-btn"
              onClick={() => openExternalBrowser('https://nerdnation.world/mobile-google-signin')}
              className="w-full flex items-center justify-center gap-3 bg-black hover:bg-gray-900 text-white font-semibold py-3 rounded-md transition-colors"
            >
              <svg className="h-5 w-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.2 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.2 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.3 34.9 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8L6.2 33C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.2C41.4 35.1 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continue with Google
            </button>
          ) : (
          <div className="relative">
            {!isRegister && (
              <span className="absolute -top-2 right-3 z-10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-md uppercase">
                Fastest
              </span>
            )}
            <div
              className="w-full flex justify-center [&>div]:!w-full [&>div]:flex [&>div]:justify-center [&_iframe]:!w-full"
              data-testid="google-login-wrapper"
            >
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  try {
                    const response = await axios.post(`${API}/api/auth/google-direct`, {
                      credential: credentialResponse.credential,
                    });
                    const { access_token, user: userData } = response.data;
                    await handleLogin(userData, access_token, true);
                    toast.success(userData.google_linked ? 'Welcome back!' : 'Welcome to Nerd Nation! 🎉 You have 30 free credits.');
                    onClose?.();
                  } catch (err) {
                    const msg = err.response?.data?.detail || 'Google sign-in failed. Please try again.';
                    toast.error(msg);
                  }
                }}
                onError={() => {
                  toast.error('Google sign-in was cancelled or failed.');
                }}
                theme="filled_black"
                size="large"
                text={isRegister ? 'signup_with' : 'continue_with'}
                shape="rectangular"
                width="320"
                useOneTap={false}
              />
            </div>
          </div>
          )}

          {/* Divider */}
          <div className="relative flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">or with email</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-sm font-medium text-gray-700">
                  Full Name
                </Label>
                <div className="relative">
                  <Input
                    id="full_name"
                    name="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={`pl-10 ${errors.full_name ? 'border-red-500' : ''}`}
                    placeholder="Enter your full name"
                    data-testid="auth-fullname-input"
                  />
                  <User className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                </div>
                {errors.full_name && (
                  <p className="text-sm text-red-600" data-testid="fullname-error">{errors.full_name}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email Address
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 text-gray-900 placeholder:text-gray-500 ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="Enter your email"
                  data-testid="auth-email-input"
                />
                <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
              </div>
              {errors.email && (
                <p className="text-sm text-red-600" data-testid="email-error">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 text-gray-900 placeholder:text-gray-500 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="Enter your password"
                  data-testid="auth-password-input"
                />
                <Lock className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 icon-small" /> : <Eye className="h-4 w-4 icon-small" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-600" data-testid="password-error">{errors.password}</p>
              )}

              {/* Forgot Password Link - Only for Login */}
              {!isRegister && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('Forgot Password clicked');
                      setShowForgotPassword(true);
                    }}
                    className="text-sm text-orange-600 hover:text-orange-700 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>

            {/* Honeypot field - Hidden from users, catches bots */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              style={{position: 'absolute', left: '-9999px', width: '1px', height: '1px'}}
              tabIndex="-1"
              autoComplete="off"
              aria-hidden="true"
            />

            {/* Two Separate Buttons */}
            <div className="flex gap-2 !mt-4">
              <Button
                type="submit"
                disabled={loading}
                className={`flex-1 font-semibold py-2 ${
                  isRegister
                    ? 'bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900'
                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'
                }`}
                data-testid="auth-submit-button"
              >
                {loading ? (
                  <div className="flex items-center space-x-2 justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isRegister ? 'Creating...' : 'Signing in...'}</span>
                  </div>
                ) : (
                  <span>{isRegister ? 'Create Account' : 'Sign In'}</span>
                )}
              </Button>

              <Button
                type="button"
                onClick={() => onSwitchMode(isRegister ? 'login' : 'register')}
                disabled={loading}
                variant="outline"
                className="flex-1 font-semibold py-2 border-2 bg-white hover:bg-gray-50 text-gray-900 border-gray-300 hover:border-gray-400"
                data-testid="auth-switch-mode"
              >
                {isRegister ? 'Sign In' : 'Create Account'}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
    
    
    {/* Forgot Password Modal - Shown when Dialog is hidden */}
    {showForgotPassword && (
      <ForgotPassword
        onClose={() => {
          console.log('ForgotPassword onClose called');
          setShowForgotPassword(false);
        }}
        onSuccess={() => {
          console.log('ForgotPassword onSuccess called');
          setShowForgotPassword(false);
        }}
      />
    )}
    </>
  );
};

export default AuthModal;